import { useQuery } from "@tanstack/react-query";
import { api } from "@/services/api";

/**
 * Shared read of the signed-in advisor's user_capabilities review status.
 * Query keys match what AdvisorFTUXPage already uses for "advisor-me", so
 * this and the FTUX page's own queries dedupe/cache together instead of
 * doubling up network calls.
 */
export function useAdvisorReviewStatus(enabled: boolean) {
  const { data: advisor } = useQuery({
    queryKey: ["advisor-me"],
    queryFn: api.advisors.getAdvisor,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const { data: preference, isLoading } = useQuery({
    queryKey: ["advisor-preference", advisor?.userId],
    // 404 means "hasn't submitted preferences yet" (e.g. pre-v3 accounts
    // never backfilled with a stub row) -- a normal, permanent state, not a
    // transient error. Resolving null instead of throwing keeps it out of
    // react-query's error/retry path, which otherwise refetches on every
    // remount regardless of staleTime and can trip the endpoint's rate limit
    // when this hook mounts once per program card.
    queryFn: async () => {
      try {
        return await api.advisors.getPreference(advisor!.userId);
      } catch {
        return null;
      }
    },
    enabled: enabled && !!advisor?.userId,
    staleTime: 60 * 1000,
    retry: false,
  });

  const reviewStatus = typeof preference?.reviewStatus === "string" ? preference.reviewStatus : undefined;
  const submittedAt = typeof preference?.submittedAt === "string" ? preference.submittedAt : undefined;
  // The review step the application was at when it was rejected.
  const rejectedAtStatus =
    typeof preference?.rejectedAtStatus === "string" ? preference.rejectedAtStatus : undefined;

  return {
    reviewStatus,
    submittedAt,
    rejectedAtStatus,
    codeOfConductAccepted: !!advisor?.codeOfConductAccepted,
    isLoading,
  };
}
